import { utils } from '../../../utils';
import {
  formatDateTimeString,
  msToIanaTimezoneName,
  utcToZoned,
} from '../../../utils/dateTimeUtils';
import * as Api from '../../../weborder/Api';
import { Utilities } from '../../../weborder/Utilities';

const checkMenuSectionTimeAvailability = (
  menuSection,
  timezone,
  concessionStoreId = undefined,
): boolean => {
  if (menuSection.MenuSectionAvailability) {
    const menuSectionAvailability = menuSection.MenuSectionAvailability;
    const isMenuSectionInPeriod =
      menuSectionAvailability.AvailableTimes !== null &&
      Utilities.isInPeriod(menuSectionAvailability.AvailableTimes, timezone);

    if (
      !isMenuSectionInPeriod &&
      menuSectionAvailability.AvailabilityMode ===
        Api.MenuEntityAvailabilityMode.DisplayBasedOnTimes
    ) {
      return false;
    }
  }

  if (!menuSection.IsAvailable) {
    return false;
  }

  const currentConcessionStoreIdMismatch = concessionStoreId
    ? concessionStoreId &&
      Number(concessionStoreId) !== menuSection.ConcessionStoreId
    : false;

  if (
    menuSection.Name === 'Hidden Promotional Items' ||
    currentConcessionStoreIdMismatch
  ) {
    return false;
  }

  return true;
};

const getAvailableMenuSections = (
  menuSections,
  timezone,
  concessionStoreId = undefined,
): any[] =>
  menuSections
    .map((menuSection) =>
      checkMenuSectionTimeAvailability(menuSection, timezone, concessionStoreId)
        ? menuSection
        : null,
    )
    .filter((section) => section !== null);

const getConcessionStoreAvailability = (
  concessionStoreId,
  menuSections,
  timezone,
) => {
  if (!concessionStoreId || !menuSections || !timezone) {
    return false;
  }

  const concessionMenuSections = menuSections.filter(
    (section) => section.ConcessionStoreId === concessionStoreId,
  );
  return !!getAvailableMenuSections(concessionMenuSections, timezone);
};

const getAvailabilityByDayTime = (
  availableMenuSections,
  dayOfWeek,
  timeOfDay,
  offset = 0,
) => {
  const convertTimeStringToTime = (timeString) => {
    const [slotHour, slotMin] = timeString.split(':');
    const timeStamp = new Date();
    timeStamp.setHours(slotHour);
    timeStamp.setMinutes(slotMin);

    if (offset > 0) {
      timeStamp.setDate(timeStamp.getDate() + offset);
    }

    return timeStamp.getTime();
  };

  const availabilitySlotsToday = availableMenuSections.map((section) =>
    convertTimeStringToTime(
      section.MenuSectionAvailability.AvailableTimes[dayOfWeek].StartTime,
    ),
  );

  const checkTime = timeOfDay.getTime();
  const timeSlot = availabilitySlotsToday.find(
    (slotStartTime) => checkTime < slotStartTime,
  );

  return timeSlot;
};

const getDateMidnightTomorrow = (now) => {
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const midnightTomorrow = tomorrow.setHours(0, 0, 0, 0);
  return new Date(midnightTomorrow);
};

const getNextOpeningTime = (
  concessionMenuSections,
  timezone,
  checkTomorrow = false,
) => {
  const now = new Date();
  const timeZone = msToIanaTimezoneName(timezone);
  const utcTimeToTimeZone = utcToZoned(now, timeZone);
  const currentDayOfWeek = parseInt(
    formatDateTimeString(utcTimeToTimeZone, 'd'),
  );

  let time = now;
  let dayOfWeek = currentDayOfWeek;
  let dayOffset = 0;

  if (checkTomorrow) {
    time = getDateMidnightTomorrow(time);
    dayOfWeek = currentDayOfWeek === 6 ? 0 : currentDayOfWeek + 1;
    dayOffset = 1;
  }

  const availabilitySlot = getAvailabilityByDayTime(
    concessionMenuSections,
    dayOfWeek,
    time,
    dayOffset,
  );

  if (availabilitySlot) {
    return formatDateTimeString(availabilitySlot);
  }

  return false;
};

const getConcessionStoresAndAvailability = (menu, timezone): any[] => {
  const concessionStores = menu?.ConcessionStores;
  if (!concessionStores || !menu) {
    return [];
  }

  const enhancedConcessionStores = concessionStores.reduce((acc, store) => {
    const storeMenuSections = menu.MenuSections.filter(
      (section) => section.ConcessionStoreId === store.ConcessionStoreId,
    );
    const availableMenuSections = getAvailableMenuSections(
      storeMenuSections,
      timezone,
    );
    const hasAvailableMenuSections = availableMenuSections.length > 0;

    let openingTime;
    let isOpeningTimeTomorrow = false;
    if (!hasAvailableMenuSections) {
      openingTime = getNextOpeningTime(storeMenuSections, timezone);

      if (!openingTime) {
        const checkTomorrow = true;
        openingTime = getNextOpeningTime(
          storeMenuSections,
          timezone,
          checkTomorrow,
        );
        isOpeningTimeTomorrow = true;
      }
    }

    acc.push({
      ...store,
      isDisabled: !hasAvailableMenuSections,
      isOpeningTimeTomorrow,
      openingTime,
    });

    return acc;
  }, []);

  const openStoresFirst = utils.change.sortBy(
    enhancedConcessionStores,
    'isDisabled',
  );
  return openStoresFirst;
};

export const menuSectionUtils = {
  getAvailableMenuSections,
  getConcessionStoreAvailability,
  getConcessionStoresAndAvailability,
};
